import { AppRouterInstance } from 'next/dist/shared/lib/app-router-context.shared-runtime';

// Constants
import { SEARCH_FORM_MESSAGE, MESSAGES, ROUTES, CV_TEMPLATE_ID } from '@shared/constants';
import { ExportFormat } from '@/constants/resume';

// Types
import { CallbackAction, RequestConfig } from '@/types';
import { ActionGroup, ActionItemType, ActionKind, ActivityType } from '@shared/types';

// Utils
import { urlToBase64 } from '@shared/utils';
import { downloadPDF } from '@/utils/pdf';
import { downloadDOCX } from '@/utils/docx';
import { buildFileName } from '@/utils/resume';
import { convertResumeToTemplateProps, shouldTriggerCallback } from '@/utils';
import { AgilityIO } from '@/components/template/resume';

// Actions
import { executeActionItem, getResumeById } from '@/actions';
interface HandleActionParams {
  item: ActionItemType;
  router: AppRouterInstance;
  requestConfig?: RequestConfig;
  callback?: CallbackAction;
}
export interface ActionResult {
  success: boolean;
  error?: string;
  message?: string;
}

// Redirect action handlers
export const handleRedirectAction = async (item: ActionItemType): Promise<ActionResult> => {
  switch (item.type) {
    case ActivityType.RESUME_VIEW_MY:
    case ActivityType.RESUME_VIEW:
    case ActivityType.RESUME_UPDATE_MY:
    case ActivityType.PROJECT_VIEW:
    case ActivityType.PROJECT_UPDATE:
    case ActivityType.EMPLOYEE_UPDATE_PROFILE:
    case ActivityType.EMPLOYEE_UPDATE_MY_PROFILE:
    case ActivityType.EMPLOYEE_VIEW_MY_PROFILE:
    case ActivityType.EMPLOYEE_VIEW_PROFILE:
      return {
        success: true
      };
    default:
      return {
        success: false,
        error: SEARCH_FORM_MESSAGE.UNKNOWN_ACTION_TYPE
      };
  }
};
interface ResumeDownloadPayload {
  formattedResume: ReturnType<typeof convertResumeToTemplateProps>;
  templateId?: string;
  fileName: string;
}

/**
 * Prepares resume data for download: fetches from the API, normalizes
 * the avatar to base64, and converts it to template props.
 */
const prepareResumeForDownload = async (id: string, extension: ExportFormat, requestConfig?: RequestConfig): Promise<{
  success: false;
  error: string;
} | ({
  success: true;
} & ResumeDownloadPayload)> => {
  const {
    data: resumeData
  } = await getResumeById(id, requestConfig);
  if (!resumeData) {
    return {
      success: false,
      error: MESSAGES.DOWNLOAD_RESUME_FAILED
    };
  }
  let normalizedPreviewImage = '';
  if (resumeData.info?.avatar) {
    normalizedPreviewImage = await urlToBase64(resumeData.info.avatar);
  }
  const formattedResume = convertResumeToTemplateProps(resumeData, normalizedPreviewImage);
  return {
    success: true,
    formattedResume,
    templateId: resumeData.templateId,
    fileName: buildFileName(extension, resumeData.info?.name)
  };
};

// Download CV action handler (PDF)
export const handleDownloadCV = async (id: string, requestConfig?: RequestConfig): Promise<ActionResult> => {
  const payload = await prepareResumeForDownload(id, ExportFormat.PDF, requestConfig);
  if (!payload.success) return payload;
  await downloadPDF(<AgilityIO data={payload.formattedResume} />, payload.fileName);
  return {
    success: true,
    message: MESSAGES.DOWNLOAD_RESUME_SUCCESS
  };
};

// Download CV action handler (DOCX)
export const handleDownloadDocx = async (id: string, requestConfig?: RequestConfig): Promise<ActionResult> => {
  const payload = await prepareResumeForDownload(id, ExportFormat.DOCX, requestConfig);
  if (!payload.success) return payload;
  await downloadDOCX(payload.formattedResume, payload.fileName, CV_TEMPLATE_ID.AGILITYIO);
  return {
    success: true,
    message: MESSAGES.DOWNLOAD_RESUME_SUCCESS
  };
};

// Custom handler action
export const handleCustomAction = async (item: ActionItemType, requestConfig?: RequestConfig): Promise<ActionResult> => {
  switch (item.type) {
    case ActivityType.RESUME_DOWNLOAD:
    case ActivityType.RESUME_DOWNLOAD_MY:
      {
        const id = item.identity;
        if (!id) {
          return {
            success: false,
            error: MESSAGES.DOWNLOAD_RESUME_FAILED
          };
        }
        return await handleDownloadCV(id, requestConfig);
      }
    case ActivityType.RESUME_DOWNLOAD_DOCX:
    case ActivityType.RESUME_DOWNLOAD_MY_DOCX:
      {
        const id = item.identity;
        if (!id) {
          return {
            success: false,
            error: MESSAGES.DOWNLOAD_RESUME_FAILED
          };
        }
        return await handleDownloadDocx(id, requestConfig);
      }
    default:
      return {
        success: false,
        error: SEARCH_FORM_MESSAGE.UNKNOWN_ACTION_TYPE
      };
  }
};

// Action handler
export const handleActionItem = async (item: ActionItemType, router: AppRouterInstance, requestConfig?: RequestConfig, callback?: CallbackAction): Promise<ActionResult> => {
  const isCreatingProjectWithName = item?.type === ActivityType.PROJECT_CREATE && !!item?.name;
  const formalizedIdentity = isCreatingProjectWithName ? item?.identity?.toLowerCase().replace(/[^a-z0-9]+/g, '') : item?.identity?.toLowerCase();
  const {
    error
  } = await executeActionItem({
    ...item,
    identity: formalizedIdentity,
    ...(isCreatingProjectWithName ? {
      name: item?.name
    } : {})
  }, requestConfig);
  if (error) {
    return {
      success: false,
      error: SEARCH_FORM_MESSAGE.EXECUTE_ACTION_FAILED
    };
  }
  switch (item.type) {
    case ActivityType.PROJECT_CREATE:
      // Handle project creation navigation
      if (item.group === ActionGroup.PROJECT) {
        router.push(`${ROUTES.PROJECTS}/${formalizedIdentity}`);
      }
      break;
    default:
      // No additional handling needed for other actions
      break;
  }
  if (shouldTriggerCallback(item) && callback?.onSuccess) {
    callback?.onSuccess?.();
  }
  return {
    success: true,
    message: SEARCH_FORM_MESSAGE.EXECUTE_ACTION_SUCCESS
  };
};

// Suggestion action handler
export const handleSuggestionAction = (): ActionResult => {
  // TODO: Implement suggestion action handling
  return {
    success: true
  };
};

// Main action handler that delegates to specific handlers
export const handleAction = async ({
  item,
  router,
  requestConfig,
  callback
}: HandleActionParams): Promise<ActionResult> => {
  switch (item.kind) {
    case ActionKind.REDIRECT:
      return await handleRedirectAction(item);
    case ActionKind.HANDLER:
      return await handleCustomAction(item, requestConfig);
    case ActionKind.ACTION:
      return await handleActionItem(item, router, requestConfig, callback);
    case ActionKind.SUGGESTION:
      return handleSuggestionAction();
    default:
      return {
        success: false,
        error: SEARCH_FORM_MESSAGE.UNKNOWN_ACTION_TYPE
      };
  }
};